import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Container, makeStyles, useTheme, TableRow,
  TableCell, Grid, Button, IconButton,
} from '@material-ui/core';
import EmailIcon from '@material-ui/icons/Email';
import { useSelector, useDispatch } from 'react-redux';

import moment from 'moment';
import { Auth0Context } from '../../utils/auth0';
import LoadingErrorContainer from '../../components/LoadingErrorContainer';
import {
  loadPatients, selectPatients,
} from '../../store/slices/patientsSlice';
import { getFunctionPromisScore } from '../../utils/survey';
import { tableIcons } from '../../config/table';
import PromisScore from '../../components/PromisScore';
import { PatientData } from '../../generated-api';
import MaterialTableWithHeaders from
  '../../components/MaterialTableWithHeaders';
import PromisKey from '../../components/PromisKey';
import { exportCsv, CustomColumn } from '../../utils/tables';
import { selectOrganizations } from '../../store/slices/organizationsSlice';

enum NumericRiskCategory {
  Low = 1,
  Moderate = 2,
  High = 3,
}

interface PatientRow extends PatientData {
  programStartDate: Date;
  latestPromisDate: Date;
  latestPromisPain: number;
  latestPromisPainChange: number | null;
  latestPromisFunction: number;
  latestPromisFunctionChange: number | null;
  riskCategory: NumericRiskCategory;
}

export default function PatientsScreen() {
  const classes = useStyles();
  const theme = useTheme();
  const { user, getAccessTokenSilently } = useAuth0() as Auth0Context;

  const dispatch = useDispatch();
  const {
    patients, loading, error,
  } = useSelector(selectPatients);
  const { myOrganization } = useSelector(selectOrganizations);

  useEffect(() => {
    dispatch(loadPatients(getAccessTokenSilently));
  }, [getAccessTokenSilently]);

  const pRows = patients == null ? null : patients.filter((p) => !(p == null))
    .map(
      (patient): PatientRow => {
        const firstSurvey = patient.surveys[0];
        const latestSurvey = patient.surveys[patient.surveys.length - 1];
        return {
          ...patient,
          programStartDate: firstSurvey.date,
          latestPromisDate: latestSurvey.date,
          latestPromisPain: latestSurvey.promisScores.painInterference,
          latestPromisPainChange: latestSurvey === firstSurvey ? null : (
            latestSurvey.promisScores.painInterference
            - firstSurvey.promisScores.painInterference
          ),
          latestPromisFunction:
            getFunctionPromisScore(latestSurvey, patient.program),
          latestPromisFunctionChange: latestSurvey === firstSurvey ? null : (
            getFunctionPromisScore(latestSurvey, patient.program)
            - getFunctionPromisScore(firstSurvey, patient.program)
          ),
          riskCategory: {
            low: NumericRiskCategory.Low,
            moderate: NumericRiskCategory.Moderate,
            high: NumericRiskCategory.High,
          }[firstSurvey.riskCategory!],
        };
      },
    );

  const cellStyle: any = {
    color: null,
    fontSize: null,
    fontFamily: null,
    fontWeight: null,
  };

  const emailPatientsQuery = Object.entries({
    bcc: patients == null ? '' : patients
      .filter((patient) => patient.sessionsPastWeek === 0)
      .filter((patient) => {
        const diff = Math.abs(moment(patient.programStartedAt)
          .diff(moment.now(), 'weeks'));
        return diff <= 10;
      })
      .map((patient) => patient.email)
      .join(','),
    subject: 'Physician Check-In on your Home-Exercise Program',
    body: `Hello,\n\nThis is ${myOrganization?.name}. We wanted to check in `
      + 'with you as we noticed you have not recently completed an exercise '
      + 'therapy session within the Limber Health app.\n\n'
      + 'As a reminder, we would like to emphasize that consistency is the key '
      + 'to long-term progress. Unfortunately, exercise therapy can take time '
      + 'to be effective. Please visit the Limber Health app to complete your '
      + 'next session.\n\n'
      + 'If you are not feeling better, please call us back and we can discuss '
      + 'next steps in your care.\n\n'
      + `In health,\n${myOrganization?.name}`,
  }).map((kv) => kv.map(encodeURIComponent).join('=')).join('&');
  const emailPatientsHref = `mailto:${user?.email}?${emailPatientsQuery}`;

  return (
    <Container className={classes.container}>
      <LoadingErrorContainer
        loading={pRows == null && loading}
        error={error && 'An error has occured.'}
        className={(!(loading && pRows == null) && !error)
          ? classes.tableContainer : undefined}
      >
        <Grid container className={classes.controlsContainer}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="primary"
              component="a"
              href={emailPatientsHref}
              startIcon={<EmailIcon />}
            >
              Email Patients with No Sessions in the Past 7 Days
            </Button>
          </Grid>
          <Grid item xs={6} className={classes.controlsRight}>
            <PromisKey />
          </Grid>
        </Grid>
        {pRows != null && (
          <MaterialTableWithHeaders
            columns={[
              {
                title: 'First Name',
                field: 'firstName',
                searchable: true,
                type: 'string',
                cellStyle,
              },
              {
                title: 'Last Name',
                field: 'lastName',
                searchable: true,
                type: 'string',
                cellStyle,
              },
              {
                title: 'Program',
                field: 'program',
                filtering: true,
                type: 'string',
                cellStyle,
              },
              {
                title: 'Email Patient',
                field: 'email',
                render: (row) => (
                    <IconButton component="a" href={`mailto:${row.email}`} >
                      <EmailIcon />
                    </IconButton>
                ),
                type: 'string',
                cellStyle,
              },
              {
                title: 'Total Sessions Completed',
                field: 'sessionsTotal',
                type: 'numeric',
                cellStyle,
                defaultSort: 'desc',
              },
              {
                title: 'Last 7 Days',
                field: 'sessionsPastWeek',
                type: 'numeric',
                cellStyle,
              },
              {
                title: 'Date',
                exportTitle: 'Date of Most Recent Assessment',
                field: 'latestPromisDate',
                type: 'date',
                cellStyle,
              },
              {
                title: 'Pain',
                field: 'latestPromisPain',
                type: 'numeric',
                render: (row) => (
                  <PromisScore
                    score={row.latestPromisPain}
                    type="pain"
                    patientHistory={row}
                  />
                ),
                cellStyle,
              },
              {
                title: 'Change',
                exportTitle: 'Change from Baseline',
                field: 'latestPromisPainChange',
                type: 'numeric',
                render: (row) => row.latestPromisPainChange != null && (
                  <PromisScore
                    score={row.latestPromisPainChange}
                    change
                    type="pain"
                    patientHistory={row}
                  />
                ),
                cellStyle,
              },
              {
                title: 'Function',
                field: 'latestPromisFunction',
                type: 'numeric',
                render: (row) => (
                  <PromisScore
                    score={row.latestPromisFunction}
                    type="function"
                    patientHistory={row}
                  />
                ),
                cellStyle,
              },
              {
                title: 'Change',
                exportTitle: 'Change from Baseline',
                field: 'latestPromisFunctionChange',
                type: 'numeric',
                render: (row) => row.latestPromisFunctionChange != null && (
                  <PromisScore
                    score={row.latestPromisFunctionChange}
                    change
                    type="function"
                    patientHistory={row}
                  />
                ),
                cellStyle,
              },
              {
                title: 'Risk Category at Baseline',
                field: 'riskCategory',
                lookup: {
                  [NumericRiskCategory.Low]: 'Low',
                  [NumericRiskCategory.Moderate]: 'Moderate',
                  [NumericRiskCategory.High]: 'High',
                },
                customSort: (row1, row2) =>
                  row1.riskCategory - row2.riskCategory,
                type: 'string',
                cellStyle,
              },
            ] as Array<CustomColumn<PatientRow>>}
            data={pRows}
            title="All Patients"
            icons={tableIcons}
            extraHeaders={(
              <TableRow className={classes.headerGroupsRow}>
                <TableCell colSpan={4} />
                <TableCell colSpan={2} className={classes.headerGroupCell}>
                  <span>Exercise Therapy Adherence</span>
                </TableCell>
                <TableCell colSpan={5} className={classes.headerGroupCell}>
                  <span>Most Recent PROMIS® Outcome Measure</span>
                </TableCell>
                <TableCell colSpan={1} />
              </TableRow>
            )}
            options={{
              exportButton: true,
              exportAllData: true,
              exportCsv: exportCsv('Limber Patients'),
              pageSize: 50,
              pageSizeOptions: [20, 50, 100],
            }}
          />
        )}
      </LoadingErrorContainer>
    </Container>
  );
}

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(3) + 64,
    paddingBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
  },
  tableContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '& > .MuiPaper-root:last-child': {
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    '& > .MuiPaper-root:last-child > div:nth-child(2) > div > div': {
      overflowY: 'visible !important',
    },
    '& th': {
      position: 'sticky',
      backgroundColor: theme.palette.background.paper,
      zIndex: 1,
    },
    '& thead > tr:first-child > th': {
      top: 0,
    },
    '& thead > tr:last-child > th': {
      top: 40,
    },
  },
  controlsContainer: {
    marginBottom: theme.spacing(2),
  },
  controlsRight: {
    textAlign: 'right',
  },
  headerGroupsRow: {
    '& th': {
      fontWeight: 'bold',
      textAlign: 'center',
      borderBottom: 'none',
      paddingBottom: 0,
    },
  },
  headerGroupCell: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    position: 'relative',
    whiteSpace: 'nowrap',
    '&::before': {
      borderTop: `1px solid ${theme.palette.grey[500]}`,
      content: '""',
      margin: '0 auto',
      position: 'absolute',
      top: `calc(0.75em + ${theme.spacing(2)}px)`,
      left: 0,
      right: 0,
      bottom: 0,
      width: '95%',
    },
    '& > span': {
      backgroundColor: theme.palette.background.paper,
      position: 'relative',
      zIndex: 1,
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
    },
  },
}));
